import Vue from 'vue'
import domUtils from 'util/dom-utils'
import { hostsConfig } from 'init/config'
import msg from 'services/msg'
import { CatalogItem, Media } from 'services/api-service'

export type SubmissionPageName =
    'add-captions'
  | 'add-video'
  | 'do-more'
  | 'registration'
  | 'review-form'
  | 'seller-rating-form'
  | 'share-media'
  | 'solo-question'

export interface SubmissionPage {
  page: SubmissionPageName
  props: any
}

export type StartAction = 'visual' | 'review'

export enum Source {
  RSE = 0,
  DO_MORE = 1,
  INLINE = 2
}

export interface LaunchData {
  alreadyReviewed?: boolean
  fromSso?: boolean
  hasErrors?: boolean
  incentiveType?: string
  reviewId?: number // when inbox submission successful
  rseToken?: string
  ssoToken?: string
  ssoProfileToken?: string
  showGuidelines?: boolean
  source?: Source
  starSelected?: number
  startAction?: StartAction
  campaign?: String
}

export type SoloQuestionView =
  'doMore' |
  'answer'|        // from question email to answer a question
  'replyToAnswer' |  // from answer email to reply to an answer
  'replyToReply' |  // from reply email to reply to the reply
  'seeAll'  // see all answers from answer or reply email

export interface QaLaunchData {
  answerText?: string, // only used coming from inbox submission
  emailToken?: string // token coming from email
  iqrlId?: number
  openReplyForAnswerId?: number
  openReplyForReplyId?: number
  questionId?: number // only used coming from do-more
  ssoProfileToken?: string
  view: SoloQuestionView
}

export interface SellerRatingLaunchData {
  emailToken?: string // token coming from SRSE
  ssoProfileToken?: string
  starSelected?: number
  transactionId?: number // populated in review-form coming from RSE if applicable
}

interface IStoreState {
  addedMedia: Array<Media>
  catItem: CatalogItem
  currentPage: SubmissionPage
  isLaunchingWriteReview: boolean // used to show/hide spinner for write review button(s)
  launchData: LaunchData
  qaLaunchData: QaLaunchData
  sellerRatingLaunchData: SellerRatingLaunchData
  isOpen: boolean
  launchingElem: HTMLElement
  pageHistory: Array<SubmissionPage>
  prevLaunchToken: string
  sku: string
  siteKey: string
}

const DEFAULT_STATE = JSON.stringify({
  addedMedia: [],
  catItem: null,
  currentPage: null,
  isLaunchingWriteReview: false,
  launchData: {},
  launchingElem: undefined,
  qaLaunchData: {},
  pageHistory: [],
  prevLaunchToken: null,
  isOpen: false,
  sellerRatingLaunchData: {},
  sku: '',
  siteKey: hostsConfig.siteKey
})

class SubmissionStore {
  state: IStoreState = JSON.parse(DEFAULT_STATE)
  eventBus: Vue = new Vue()

  addMedia(media: Array<Media>) {
    media.forEach(item => this.state.addedMedia.push(item))
  }

  close() {
    if (this.state.launchingElem) {
      this.state.launchingElem.focus()
    } else {
      domUtils.safeFocus(document.body)
    }
    this.state.isOpen = false
  }

  // returns the index of the item removed
  removeMedia(mediaItem: Media) {
    for (let i = 0; i < this.state.addedMedia.length; i++) {
      const thisItem = this.state.addedMedia[i]
      if (mediaItem.id === thisItem.id) {
        this.state.addedMedia.splice(i, 1)
        return i
      }
    }
    return -1
  }

  get addedMedia(): Array<Media> {
    return this.state.addedMedia
  }

  get catItem(): CatalogItem {
    return this.state.catItem
  }

  set catItem(catItem: CatalogItem) {
    this.state.catItem = catItem
    this.state.sku = catItem.sku
  }

  get currentPage(): SubmissionPage {
    return this.state.currentPage
  }

  set currentPage(page: SubmissionPage) {
    // put previous page into history if there was one.  Not adding 'add-video' since it is not
    // a page the user can go back to.  Should be going back to the one before that.
    if (this.state.currentPage && this.state.currentPage.page !== 'add-video') {
      const newLength = this.state.pageHistory.unshift(this.state.currentPage)
      // only keep history up to previous 3 pages.  Shouldn't need but 2 or 3
      if (newLength > 3) {
        this.state.pageHistory.splice(3)
      }
    }
    // Remove any 'back' listener from the eventBus to make sure they are cleaned up before going
    // to the next page
    this.eventBus.$off('back')
    this.state.currentPage = page
  }

  // a way for the submission modal to communicate to the screen widget that a "close" event has
  // taken place, which may mean "go back" to the previous modal screen.  The inidividual widget
  // can determine where to go back to or fire a close event back up to the submission modal.
  fireBackEvent() {
    // this is using the internal Vue api, which could change, but not likely -
    // https://forum.vuejs.org/t/detect-whether-there-are-event-listeners/10754
    // using to detect if the 'back' event is registered, if not, we can just close
    const events = (this.eventBus as any)._events
    if (events && events.back) {
      this.eventBus.$emit('back')
      return
    }
    this.close()
  }

  getLaunchToken(): string {
    return this.state.launchData.rseToken || this.state.qaLaunchData.emailToken ||
      this.state.sellerRatingLaunchData.emailToken || this.state.launchData.ssoToken ||
      this.state.prevLaunchToken
  }

  get isLaunchingWriteReview(): boolean {
    return this.state.isLaunchingWriteReview
  }

  set isLaunchingWriteReview(isLaunching: boolean) {
    this.state.isLaunchingWriteReview = isLaunching
  }

  isVisualFirstFlow(): boolean {
    return this.state.launchData.startAction === 'visual'
  }

  get launchData(): LaunchData {
    return this.state.launchData
  }

  set launchData(launchData: LaunchData) {
    this.state.launchData = launchData
  }

  get qaLaunchData(): QaLaunchData {
    return this.state.qaLaunchData
  }

  set qaLaunchData(launchData: QaLaunchData) {
    this.state.qaLaunchData = launchData
  }

  get sellerRatingLaunchData(): SellerRatingLaunchData {
    return this.state.sellerRatingLaunchData
  }

  set sellerRatingLaunchData(launchData: SellerRatingLaunchData) {
    this.state.sellerRatingLaunchData = launchData
  }

  get isOpen(): boolean {
    return this.state.isOpen
  }

  set isOpen(isOpen: boolean) {
    this.state.isOpen = isOpen
  }

  get pageHistory(): Array<SubmissionPage> {
    return this.state.pageHistory
  }

  get poweredByText(): string {
    const page = this.state.currentPage ? this.state.currentPage.page : null
    if (page === 'do-more') {
      return msg('doMore.poweredBy')
    } else if (page === 'review-form' && !this.isVisualFirstFlow()) {
      return msg('submission.reviewsPoweredBy')
    }
    return msg('submission.visualPoweredBy')
  }

  get previousPage(): SubmissionPage {
    return this.state.pageHistory.length > 0 ? this.state.pageHistory[0] : null
  }

  // This sets to all default values including isOpen false which will close the submission modal.
  // So only use when not staying in the modal
  resetStore() {
    this.state = JSON.parse(DEFAULT_STATE)
    // make sure all registered events ($on) are removed from the eventBus
    this.eventBus.$off()
  }

  // Resets to default values except:
  // - isOpen true so the modal does not close
  // - the catItem and siteKey
  // - launchingElem if present
  // - currentPage and pageHistory if present
  resetStoreWithCatItem(siteKey: string, catItem: CatalogItem) {
    const prevLaunchToken = this.getLaunchToken()
    const launchingElem = this.state.launchingElem
    const history = this.state.pageHistory
    const currentPage = this.state.currentPage
    const defaultState = JSON.parse(DEFAULT_STATE)
    defaultState.isOpen = true
    defaultState.siteKey = siteKey
    defaultState.launchingElem = launchingElem
    defaultState.currentPage = currentPage
    defaultState.pageHistory = history
    defaultState.prevLaunchToken = prevLaunchToken
    this.state = defaultState
    this.catItem = catItem
  }

  // setting sku is done through one of the startFlow functions
  get sku(): string {
    return this.state.sku
  }

  get siblingSiteKey(): string {
    return this.state.siteKey !== hostsConfig.siteKey ? this.state.siteKey : ''
  }

  // setting siteKey is done through one of the startFlow functions
  get siteKey(): string {
    return this.state.siteKey
  }

  // start a flow with Do More or for use by other start flow functions
  startFlow(sku: string, siteKey: string, page: SubmissionPage) {
    this.state.sku = sku
    this.state.siteKey = siteKey
    this.currentPage = page
  }

  // start a flow with QA Solo View
  startQaFlow(sku: string, siteKey: string, page: SubmissionPage, launchData: QaLaunchData) {
    this.state.qaLaunchData = launchData
    this.startFlow(sku, siteKey, page)
  }

  // start a flow with review form or vc share
  startRevFlow(
    sku: string,
    siteKey: string,
    page: SubmissionPage,
    launchData: LaunchData,
    launchingElem: HTMLElement = undefined) {
    this.state.launchData = launchData
    // don't want body to be the launching element
    if (launchingElem && launchingElem.tagName.toLowerCase() !== 'body') {
      this.state.launchingElem = launchingElem
    }
    this.startFlow(sku, siteKey, page)
  }

  startSellerRatingFlow(
    siteKey: string,
    page: SubmissionPage,
    launchData: SellerRatingLaunchData
  ) {
    this.state.sellerRatingLaunchData = launchData
    this.startFlow(null, siteKey, page)
  }

  // start a flow with Do More using catItem instead of sku
  startFlowWithCatItem(catItem: CatalogItem, siteKey: string, page: SubmissionPage) {
    this.state.catItem = catItem
    this.startFlow(catItem.sku, siteKey, page)
  }
}

export default new SubmissionStore()
